import { organizerMessages } from './organizerMessages';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { BoxInfo, CardInfo, FullCardInfo, MessageInfo } from '@lub0v/santa-utils';
import { santaClient } from '@src/state/client';

export type ConversationId = 'org' | 'me' | 'giftee' | `user-${number}` | `org-${number}`;
export function loadConversation(key: string, id: ConversationId) {
  if (id.startsWith('org-')) return organizerMessages(key, Number(id.slice(4)));
  return id === 'org'
    ? santaClient.getOrgMessages({ key })
    : id === 'me'
      ? santaClient.getSantaMessages({ key })
      : id === 'giftee'
        ? santaClient.getWardMessages({ key })
        : santaClient.getOrgMessagesWithUser({ key, id: Number(id.slice(5)) });
}
export function unreadCount(messages: MessageInfo[] = []) {
  return messages.filter((m) => !m.isYou && !m.readAt && m.active !== false).length;
}
export interface ConversationSummary {
  id: ConversationId;
  unread: number;
  last: Pick<MessageInfo, 'id' | 'message' | 'timestamp' | 'isYou'>;
}
function summarize(id: ConversationId, messages: MessageInfo[]): ConversationSummary | undefined {
  const last = messages.filter(m => m.active !== false)
    .sort((a, b) => Number(a.timestamp) - Number(b.timestamp) || a.id - b.id).at(-1);
  return last ? { id, unread: unreadCount(messages), last: { id: last.id, message: last.message?.slice(0, 200), timestamp: last.timestamp, isYou: last.isYou } } : undefined;
}
const InboxContext = createContext<{
  conversations: Partial<Record<ConversationId, MessageInfo[]>>;
  summaries: Partial<Record<ConversationId, ConversationSummary>>;
  loaded: boolean;
  update: (id: ConversationId, messages: MessageInfo[]) => void;
  markRead: (id: ConversationId, messageId: number) => void;
}>({ conversations: {}, summaries: {}, loaded: false, update: () => {}, markRead: () => {} });
export const useMessageInbox = () => useContext(InboxContext);

export function MessageInboxProvider({ info, children }: {
  info: BoxInfo; cards: CardInfo[]; card: FullCardInfo | null; ward: FullCardInfo | null; children: ReactNode;
}) {
  const identity = `${info.box.key}:${info.cardId}:${info.isAdmin}:${info.isParticipant}:${info.isActiveDraw}`;
  const [state, setState] = useState<{
    identity: string; conversations: Partial<Record<ConversationId, MessageInfo[]>>;
    summaries: Partial<Record<ConversationId, ConversationSummary>>; loaded: boolean;
  }>({ identity, conversations: {}, summaries: {}, loaded: false });
  const revision = useRef(0);
  if (state.identity !== identity) setState({ identity, conversations: {}, summaries: {}, loaded: false });
  useEffect(() => {
    const controller = new AbortController();
    let loading = false;
    async function refresh() {
      if (loading || document.hidden || (!info.isParticipant && !info.isAdmin)) return;
      loading = true;
      const requestRevision = revision.current;
      try {
        const response = await fetch(`/api/box/${encodeURIComponent(info.box.key)}/inbox`, {
          credentials: 'same-origin', signal: controller.signal,
        });
        if (!response.ok) return;
        const rows = await response.json() as ConversationSummary[];
        if (controller.signal.aborted || requestRevision !== revision.current) return;
        setState(old => old.identity === identity ? { ...old, loaded: true,
          summaries: Object.fromEntries(rows.map(row => [row.id, row])),
        } : old);
      } catch {
        // Keep the last successful summary; the next visible poll retries.
      } finally { loading = false; }
    }
    const initialTimer = window.setTimeout(() => { void refresh(); }, 0);
    const timer = window.setInterval(refresh, 30000);
    document.addEventListener('visibilitychange', refresh);
    return () => { controller.abort(); clearTimeout(initialTimer); clearInterval(timer); document.removeEventListener('visibilitychange', refresh); };
  }, [identity, info.box.key, info.isAdmin, info.isParticipant]);
  const value = useMemo(() => ({
    conversations: state.conversations, summaries: state.summaries, loaded: state.loaded,
    markRead: (id: ConversationId, messageId: number) => {
      ++revision.current;
      setState(old => {
        if (old.identity !== identity) return old;
        const messages = (old.conversations[id] || []).map(m => m.id === messageId ? { ...m, readAt: Date.now() } : m);
        return { ...old, conversations: { ...old.conversations, [id]: messages },
          summaries: { ...old.summaries, [id]: summarize(id, messages) } };
      });
    },
    update: (id: ConversationId, messages: MessageInfo[]) => {
      ++revision.current;
      setState(old => old.identity === identity ? { ...old, conversations: { ...old.conversations, [id]: messages },
        summaries: { ...old.summaries, [id]: summarize(id, messages) } } : old);
    },
  }), [state, identity]);
  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>;
}
